import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authFetch } from '../utils/authFetch';
import { decodeToken, getAuthToken, getUserData } from '../utils/authHelpers';

export const PERSONA_HOME = {
  creator: '/creator-dashboard',
  host: '/host-dashboard',
  partner: '/partner-settings',
  traveler: '/itineraries',
  admin: '/admin',
};

export const PERSONA_LABELS = {
  creator: 'Creator',
  host: 'Host',
  partner: 'Partner',
  traveler: 'Traveler',
  admin: 'Admin',
};

const PersonaContext = createContext({
  roles: [],
  activePersona: null,
  isSwitchingPersona: false,
  switchPersona: async () => null,
  refreshPersonaFromStorage: () => {},
  getPersonaHome: () => '/dashboard',
});

function readStoredPersonaState() {
  if (typeof window === 'undefined') return { roles: [], activePersona: null };

  const token = getAuthToken();
  const decoded = decodeToken(token);
  const userData = getUserData();
  const roles = decoded?.roles || userData?.roles || [];
  const activePersona =
    decoded?.activePersona ||
    decoded?.userType ||
    userData?.activePersona ||
    userData?.userType ||
    window.localStorage.getItem('postaway_user_type') ||
    roles[0] ||
    null;

  return { roles, activePersona };
}

export const PersonaProvider = ({ children }) => {
  const initial = readStoredPersonaState();
  const [roles, setRoles] = useState(initial.roles);
  const [activePersona, setActivePersona] = useState(initial.activePersona);
  const [isSwitchingPersona, setIsSwitchingPersona] = useState(false);

  // Track the latest values in refs so switchPersona can stay a stable
  // reference. Otherwise its identity changes on every persona/roles update,
  // re-firing consumer effects (HostDashboard / CreatorDashboard subscription
  // checks) and producing render thrash that could surface as UI flicker.
  const activePersonaRef = useRef(initial.activePersona);
  const rolesRef = useRef(initial.roles);

  useEffect(() => { activePersonaRef.current = activePersona; }, [activePersona]);
  useEffect(() => { rolesRef.current = roles; }, [roles]);

  const refreshPersonaFromStorage = useCallback(() => {
    const next = readStoredPersonaState();
    setRoles(next.roles);
    setActivePersona(next.activePersona);
  }, []);

  useEffect(() => {
    const handleStorage = () => refreshPersonaFromStorage();
    window.addEventListener('storage', handleStorage);
    window.addEventListener('postaway:persona-changed', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('postaway:persona-changed', handleStorage);
    };
  }, [refreshPersonaFromStorage]);

  const switchPersona = useCallback(async (persona) => {
    const nextPersona = String(persona || '').toLowerCase();
    const currentPersona = activePersonaRef.current;
    if (!nextPersona || nextPersona === currentPersona) return currentPersona;

    setIsSwitchingPersona(true);
    try {
      const response = await authFetch('/api/persona/switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ persona: nextPersona }),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'persona_switch_failed');
      }

      if (data.token) localStorage.setItem('postaway_auth_token', data.token);
      if (data.user) localStorage.setItem('postaway_user_data', JSON.stringify(data.user));
      localStorage.setItem('postaway_user_type', data.user?.activePersona || nextPersona);

      setRoles(data.user?.roles || rolesRef.current);
      setActivePersona(data.user?.activePersona || nextPersona);
      window.dispatchEvent(new Event('postaway:persona-changed'));
      return data.user?.activePersona || nextPersona;
    } finally {
      setIsSwitchingPersona(false);
    }
  }, []);

  const getPersonaHome = useCallback(
    (persona) => PERSONA_HOME[persona || activePersonaRef.current] || '/dashboard',
    []
  );

  const value = useMemo(() => ({
    roles,
    activePersona,
    isSwitchingPersona,
    switchPersona,
    refreshPersonaFromStorage,
    getPersonaHome,
  }), [roles, activePersona, isSwitchingPersona, switchPersona, refreshPersonaFromStorage, getPersonaHome]);

  return (
    <PersonaContext.Provider value={value}>
      {children}
    </PersonaContext.Provider>
  );
};

export const usePersona = () => useContext(PersonaContext);

export default PersonaContext;
